"use client";

import { c as _c } from "react/compiler-runtime";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Container, SectionSubHeading, SectionTitle, SelectedWorkCard } from "@/components";
import { caseStudyData } from "@/data/CaseStudyData";
import { useIsDesktop } from "@/hooks/ui/useIsDesktop";
import { useSectionProgress } from "@/hooks/animations/useSectionProgress";
import { cardRise, cardStagger, fadeInUp } from "@/lib/animation/variant";
const SelectedWork = () => {
  const $ = _c(19);
  const sectionRef = useRef(null);
  const railRef = useRef(null);
  const [distance, setDistance] = useState(0);
  const isDesktop = useIsDesktop();
  const progress = useSectionProgress(sectionRef, isDesktop && distance > 0);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const rail = railRef.current;
      if (!rail) {
        return;
      }
      let frame = 0;
      const measure = () => {
        frame = 0;
        setDistance(Math.max(0, rail.scrollWidth - window.innerWidth + 48));
      };
      const schedule = () => {
        if (frame) {
          return;
        }
        frame = requestAnimationFrame(measure);
      };
      const observer = new ResizeObserver(schedule);
      observer.observe(rail);
      window.addEventListener("resize", schedule, {
        passive: true
      });
      return () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
        window.removeEventListener("resize", schedule);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== distance || $[3] !== isDesktop) {
    t2 = isDesktop && distance > 0 ? {
      height: `calc(${distance}px + 100vh)`
    } as CSSProperties : undefined;
    $[2] = distance;
    $[3] = isDesktop;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      once: true
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <motion.div custom={0} initial="hidden" whileInView="visible" viewport={t3} variants={fadeInUp}><SectionSubHeading subtitle="Selected work" /></motion.div>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      once: true
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Container className="space-y-2 px-4 lg:px-10 lg:pt-12">{t4}<motion.div custom={1} initial="hidden" whileInView="visible" viewport={t5} variants={fadeInUp}><SectionTitle title="Featured projects, built and measured." className="max-w-4xl lg:text-4xl xl:text-5xl" /></motion.div></Container>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      once: true,
      amount: 0.1
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Container className="px-4 pt-16 lg:hidden"><motion.div initial="hidden" whileInView="show" viewport={t7} variants={cardStagger} className="grid gap-5 md:grid-cols-2">{caseStudyData.map(_temp)}</motion.div></Container>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = `translate3d(${-progress * distance}px,0,0)`;
  let t10;
  if ($[11] !== t9) {
    t10 = {
      transform: t9
    };
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = caseStudyData.map(_temp2);
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t10) {
    t12 = <div className="lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col lg:overflow-hidden">{t6}{t8}<div className="hidden flex-1 items-center pt-10 pb-12 lg:flex"><div ref={railRef} style={t10} className="flex items-center gap-6 px-10 will-change-transform">{t11}</div></div></div>;
    $[14] = t10;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t12 || $[17] !== t2) {
    t13 = <section ref={sectionRef} id="selected-work" style={t2} className="relative w-full bg-background py-20 md:py-24 lg:my-28 lg:py-0" aria-label="Selected work">{t12}</section>;
    $[16] = t12;
    $[17] = t2;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  return t13;
};
export default SelectedWork;
function _temp(caseStudy) {
  return <motion.div key={caseStudy.id} variants={cardRise}><SelectedWorkCard data={caseStudy} className="h-130 md:h-145" /></motion.div>;
}
function _temp2(caseStudy_0, index) {
  return <div key={caseStudy_0.id} className="w-[min(88vw,880px)] shrink-0"><SelectedWorkCard data={caseStudy_0} className={index % 2 === 0 ? "lg:h-[66vh]" : "lg:h-[calc(66vh-var(--spacing)*6)]"} /></div>;
}